import React, { useState } from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    Button,
    HStack,
    Icon,
    SimpleGrid,
    Badge,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import {
    FaCheckCircle,
    FaCalculator,
    FaWallet,
    FaPrint,
    FaArrowRight,
    FaGraduationCap,
} from "react-icons/fa";
import PublicNavbar from "../Landing/PublicNavbar";
import PublicFooter from "../Landing/PublicFooter";
import {
    DoodleUnderline,
    DoodleSparkle,
    DoodleStar,
    WashiTape,
    DoodlePaperclip,
} from "../../CustomUi/DoodleIcons";
import { APP_REGISTER_URL } from "../../../services/constants";

import { usePageTitle } from "../../../hooks/usePageTitle";

type DemoTab = "results" | "report-card" | "finance";

const DemoPage: React.FC = () => {
    usePageTitle(
        "Interactive Demo & Simulator",
        "Test-drive Result's automated result entry, report card generation, and dedicated school fee virtual accounts live in your browser."
    );
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<DemoTab>("results");

    return (
        <Box minH="100vh" bg="#FAF8F2">
            <PublicNavbar />

            {/* Hero */}
            <Box py={[14, 20]} bg="brand.900" color="white" position="relative" overflow="hidden">
                <Container maxW="5xl">
                    <VStack gap={5} textAlign="center">
                        <HStack gap={2} justify="center">
                            <DoodleSparkle size={20} color="#FBBF24" />
                            <Box
                                px={3.5}
                                py={1}
                                bg="whiteAlpha.150"
                                borderRadius="full"
                                border="1px solid"
                                borderColor="whiteAlpha.200"
                            >
                                <Text fontSize="xs" fontWeight="bold" letterSpacing="widest" textTransform="uppercase" color="gold.300">
                                    Interactive Tour
                                </Text>
                            </Box>
                            <DoodleSparkle size={20} color="#FBBF24" />
                        </HStack>

                        <Heading as="h1" fontSize={["3xl", "4xl", "5xl"]} fontWeight="extrabold" lineHeight="1.15">
                            See how Result makes school work{" "}
                            <Box as="span" position="relative" display="inline-block" color="gold.400">
                                playful &amp; easy
                                <DoodleUnderline position="absolute" bottom="-8px" left="0" color="#F59E0B" height="14px" />
                            </Box>
                        </Heading>

                        <Text fontSize={["md", "lg"]} opacity={0.8} maxW="2xl" lineHeight="1.7">
                            No manual grading spreadsheets. No three-day printing marathons. Click through the interactive demo tabs below to see how it works!
                        </Text>
                    </VStack>
                </Container>
            </Box>

            {/* Interactive Showcase Container */}
            <Box py={[12, 16]} className="notebook-dots">
                <Container maxW="6xl">
                    <VStack gap={8}>
                        {/* Interactive Tab Switcher styled as notebook tabs */}
                        <HStack gap={3} flexWrap="wrap" justify="center">
                            {[
                                { id: "results", label: "1. Grade & Result Entry 📝", icon: FaCalculator },
                                { id: "report-card", label: "2. Printable Report Card 🖨️", icon: FaPrint },
                                { id: "finance", label: "3. School Fee Collection 💳", icon: FaWallet },
                            ].map((tab) => {
                                const isCurrent = activeTab === tab.id;
                                return (
                                    <Button
                                        key={tab.id}
                                        size="md"
                                        bg={isCurrent ? "brand.800" : "white"}
                                        color={isCurrent ? "white" : "brand.900"}
                                        border="2px solid #0A3D2E"
                                        boxShadow={isCurrent ? "4px 4px 0px #0A3D2E" : "2px 2px 0px #0A3D2E"}
                                        fontWeight="bold"
                                        borderRadius="xl"
                                        px={5}
                                        transition="all 0.15s ease"
                                        _hover={{
                                            transform: "translate(-2px, -2px)",
                                            boxShadow: "5px 5px 0px #0A3D2E",
                                        }}
                                        onClick={() => setActiveTab(tab.id as DemoTab)}
                                    >
                                        <Icon as={tab.icon} mr={2} fontSize="sm" />
                                        {tab.label}
                                    </Button>
                                );
                            })}
                        </HStack>

                        {/* Interactive Screen Preview Box */}
                        <Box
                            w="full"
                            position="relative"
                            bg="white"
                            borderRadius="2xl"
                            border="3px solid #0A3D2E"
                            boxShadow="6px 8px 0px #0A3D2E"
                            p={[4, 8]}
                            overflow="hidden"
                        >
                            {/* Washi tape on top corner */}
                            <WashiTape angle={-3} top="-10px" left="28px" tapeColor="#FEF08A" label="Interactive Preview ✨" />
                            <DoodlePaperclip color="#10B981" size={38} position="absolute" top="-14px" right="36px" />

                            {/* TAB 1: RESULT ENTRY GRID */}
                            {activeTab === "results" && (
                                <VStack align="stretch" gap={5} pt={4}>
                                    <HStack justify="space-between" flexWrap="wrap" gap={2}>
                                        <VStack align="start" gap={0.5}>
                                            <Heading as="h3" fontSize="lg" fontWeight="extrabold" color="gray.900">
                                                JSS 2A — Mathematics Result Entry Sheet
                                            </Heading>
                                            <Text fontSize="xs" color="gray.500">
                                                2024/2025 Academic Session · Second Term · Form Teacher: Mr. Adeleke
                                            </Text>
                                        </VStack>
                                        <HStack gap={2}>
                                            <Box
                                                className="font-handwriting"
                                                fontSize="md"
                                                fontWeight="bold"
                                                color="emerald.700"
                                                bg="#DCFCE7"
                                                px={3}
                                                py={1}
                                                borderRadius="lg"
                                                border="1.5px solid #0A3D2E"
                                            >
                                                Auto-Graded Instantly! 🎯
                                            </Box>
                                        </HStack>
                                    </HStack>

                                    {/* Mock Spreadsheet Table */}
                                    <Box overflowX="auto" border="2px solid #0A3D2E" borderRadius="xl">
                                        <Box as="table" w="full" fontSize="sm" style={{ borderCollapse: "collapse" }}>
                                            <Box as="thead" bg="#F1EFE9" borderBottom="2px solid #0A3D2E">
                                                <Box as="tr" textAlign="left" fontWeight="bold" color="gray.800">
                                                    <Box as="th" p={3}>Roll #</Box>
                                                    <Box as="th" p={3}>Student Name</Box>
                                                    <Box as="th" p={3} textAlign="center">CA 1 (20)</Box>
                                                    <Box as="th" p={3} textAlign="center">CA 2 (20)</Box>
                                                    <Box as="th" p={3} textAlign="center">Exam (60)</Box>
                                                    <Box as="th" p={3} textAlign="center" bg="#FEF9C3">Total (100)</Box>
                                                    <Box as="th" p={3} textAlign="center">Grade</Box>
                                                    <Box as="th" p={3}>Remark</Box>
                                                </Box>
                                            </Box>
                                            <Box as="tbody">
                                                {[
                                                    { roll: "01", name: "Amara Okoye", ca1: 18, ca2: 19, exam: 54, total: 91, grade: "A+", remark: "Distinction 🌟", color: "green" },
                                                    { roll: "02", name: "Chidi Nwosu", ca1: 15, ca2: 16, exam: 48, total: 79, grade: "A", remark: "Excellent work", color: "green" },
                                                    { roll: "03", name: "Fatima Bello", ca1: 14, ca2: 17, exam: 42, total: 73, grade: "B", remark: "Very Good", color: "blue" },
                                                    { roll: "04", name: "Tunde Adeyemi", ca1: 12, ca2: 13, exam: 39, total: 64, grade: "C", remark: "Good, can improve", color: "orange" },
                                                    { roll: "05", name: "Zainab Abubakar", ca1: 17, ca2: 18, exam: 52, total: 87, grade: "A+", remark: "Distinction 🌟", color: "green" },
                                                ].map((row, idx) => (
                                                    <Box key={row.roll} as="tr" borderBottom="1px solid #E2E8F0" bg={idx % 2 === 0 ? "white" : "#FAF9F5"}>
                                                        <Box as="td" p={3} fontWeight="bold" color="gray.500">{row.roll}</Box>
                                                        <Box as="td" p={3} fontWeight="bold" color="gray.900">{row.name}</Box>
                                                        <Box as="td" p={3} textAlign="center" fontWeight="medium">{row.ca1}</Box>
                                                        <Box as="td" p={3} textAlign="center" fontWeight="medium">{row.ca2}</Box>
                                                        <Box as="td" p={3} textAlign="center" fontWeight="medium">{row.exam}</Box>
                                                        <Box as="td" p={3} textAlign="center" fontWeight="extrabold" color="brand.800" bg="#FEF9C3">{row.total}</Box>
                                                        <Box as="td" p={3} textAlign="center">
                                                            <Badge colorScheme={row.color} px={2} py={0.5} borderRadius="md" fontWeight="bold">
                                                                {row.grade}
                                                            </Badge>
                                                        </Box>
                                                        <Box as="td" p={3} fontSize="xs" fontWeight="medium" color="gray.600">{row.remark}</Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    </Box>

                                    <HStack justify="space-between" flexWrap="wrap" pt={2}>
                                        <HStack gap={2} className="font-handwriting" fontSize="md" color="amber.800">
                                            <DoodleStar size={18} />
                                            <Text>Teachers can save drafts or publish directly to parents in 1 click!</Text>
                                        </HStack>
                                        <Button
                                            size="sm"
                                            bg="brand.800"
                                            color="white"
                                            borderRadius="lg"
                                            fontWeight="bold"
                                            _hover={{ bg: "brand.900" }}
                                            onClick={() => setActiveTab("report-card")}
                                        >
                                            Next: View Generated Report Card <Icon as={FaArrowRight} ml={1.5} />
                                        </Button>
                                    </HStack>
                                </VStack>
                            )}

                            {/* TAB 2: PRINTABLE REPORT CARD */}
                            {activeTab === "report-card" && (
                                <VStack align="stretch" gap={5} pt={4}>
                                    <HStack justify="space-between" flexWrap="wrap">
                                        <VStack align="start" gap={0.5}>
                                            <Heading as="h3" fontSize="lg" fontWeight="extrabold" color="gray.900">
                                                Official Term Report Card (PDF Preview)
                                            </Heading>
                                            <Text fontSize="xs" color="gray.500">
                                                Formatted with custom school crest, watermark, and official stamp
                                            </Text>
                                        </VStack>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            borderColor="#0A3D2E"
                                            color="brand.800"
                                            borderRadius="lg"
                                            fontWeight="bold"
                                        >
                                            <Icon as={FaPrint} mr={1.5} /> Export Single or Bulk PDF
                                        </Button>
                                    </HStack>

                                    {/* Mock Report Card Document */}
                                    <Box
                                        p={[4, 6]}
                                        bg="#FFFDF8"
                                        border="2px dashed #0A3D2E"
                                        borderRadius="xl"
                                        boxShadow="inner"
                                    >
                                        {/* School Header */}
                                        <VStack gap={1} textAlign="center" borderBottom="2px solid #0A3D2E" pb={4} mb={4}>
                                            <Icon as={FaGraduationCap} fontSize="3xl" color="brand.800" />
                                            <Heading as="h4" fontSize="lg" fontWeight="extrabold" color="brand.900" textTransform="uppercase">
                                                Greenfield International Academy
                                            </Heading>
                                            <Text fontSize="xs" color="gray.500">Plot 12 Victoria Island, Lagos · Motivated to Excel</Text>
                                            <Badge mt={1} colorScheme="green" px={3} py={1} borderRadius="full" fontSize="xs">
                                                TERMINAL REPORT CARD · SECOND TERM 2024/2025
                                            </Badge>
                                        </VStack>

                                        {/* Student Bio row */}
                                        <SimpleGrid columns={[2, 4]} gap={3} mb={4} fontSize="xs" bg="#F4F1EA" p={3} borderRadius="lg">
                                            <Box><Text color="gray.500">Student Name:</Text> <Text fontWeight="bold">Amara Okoye</Text></Box>
                                            <Box><Text color="gray.500">Class:</Text> <Text fontWeight="bold">JSS 2A</Text></Box>
                                            <Box><Text color="gray.500">Term Position:</Text> <Text fontWeight="bold" color="emerald.700">1st of 38 Students</Text></Box>
                                            <Box><Text color="gray.500">Attendance:</Text> <Text fontWeight="bold">58 / 60 Days (96%)</Text></Box>
                                        </SimpleGrid>

                                        {/* Subject Summary */}
                                        <Box overflowX="auto" mb={4}>
                                            <Box as="table" w="full" fontSize="xs" style={{ borderCollapse: "collapse" }}>
                                                <Box as="thead" bg="brand.900" color="white">
                                                    <Box as="tr">
                                                        <Box as="th" p={2} textAlign="left">Subject</Box>
                                                        <Box as="th" p={2} textAlign="center">CA 1</Box>
                                                        <Box as="th" p={2} textAlign="center">CA 2</Box>
                                                        <Box as="th" p={2} textAlign="center">Exam</Box>
                                                        <Box as="th" p={2} textAlign="center">Total</Box>
                                                        <Box as="th" p={2} textAlign="center">Grade</Box>
                                                        <Box as="th" p={2} textAlign="left">Teacher Remarks</Box>
                                                    </Box>
                                                </Box>
                                                <Box as="tbody">
                                                    {[
                                                        { sub: "Mathematics", ca1: 18, ca2: 19, ex: 54, tot: 91, gr: "A+", rem: "Exceptional mastery of algebra" },
                                                        { sub: "English Language", ca1: 17, ca2: 18, ex: 51, tot: 86, gr: "A", rem: "Strong writing and composition skills" },
                                                        { sub: "Basic Science", ca1: 19, ca2: 18, ex: 55, tot: 92, gr: "A+", rem: "Top scorer in practicals" },
                                                        { sub: "Civic Education", ca1: 16, ca2: 17, ex: 48, tot: 81, gr: "A", rem: "Active contributor in discussions" },
                                                    ].map((r, i) => (
                                                        <Box key={r.sub} as="tr" borderBottom="1px solid #E2E8F0" bg={i % 2 === 0 ? "white" : "#FAF9F5"}>
                                                            <Box as="td" p={2} fontWeight="bold">{r.sub}</Box>
                                                            <Box as="td" p={2} textAlign="center">{r.ca1}</Box>
                                                            <Box as="td" p={2} textAlign="center">{r.ca2}</Box>
                                                            <Box as="td" p={2} textAlign="center">{r.ex}</Box>
                                                            <Box as="td" p={2} textAlign="center" fontWeight="bold" color="brand.800">{r.tot}</Box>
                                                            <Box as="td" p={2} textAlign="center" fontWeight="bold" color="emerald.700">{r.gr}</Box>
                                                            <Box as="td" p={2} color="gray.600">{r.rem}</Box>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        </Box>

                                        {/* Remarks & Stamp */}
                                        <HStack justify="space-between" align="center" pt={3} borderTop="1.5px solid #0A3D2E" flexWrap="wrap" gap={3}>
                                            <VStack align="start" gap={1}>
                                                <Text fontSize="xs" fontWeight="bold" color="gray.700">Principal&apos;s Recommendation:</Text>
                                                <Text fontSize="xs" fontStyle="italic" color="gray.600">
                                                    &ldquo;An exemplary student whose conduct and academics inspire her peers. Promoted with honours.&rdquo;
                                                </Text>
                                            </VStack>
                                            <Box
                                                border="2px solid #E11D48"
                                                borderRadius="full"
                                                px={4}
                                                py={1.5}
                                                className="font-handwriting"
                                                fontSize="md"
                                                fontWeight="bold"
                                                color="#E11D48"
                                                transform="rotate(-5deg)"
                                            >
                                                APPROVED &amp; VERIFIED ✓
                                            </Box>
                                        </HStack>
                                    </Box>
                                </VStack>
                            )}

                            {/* TAB 3: FINANCE & VIRTUAL ACCOUNTS */}
                            {activeTab === "finance" && (
                                <VStack align="stretch" gap={5} pt={4}>
                                    <HStack justify="space-between" flexWrap="wrap">
                                        <VStack align="start" gap={0.5}>
                                            <Heading as="h3" fontSize="lg" fontWeight="extrabold" color="gray.900">
                                                Result Finance &amp; Virtual Accounts
                                            </Heading>
                                            <Text fontSize="xs" color="gray.500">
                                                Each student gets a dedicated account number. Real-time reconciliation.
                                            </Text>
                                        </VStack>
                                        <Badge colorScheme="green" px={3} py={1} borderRadius="full" fontSize="xs">
                                            LIVE SETTLEMENT READY
                                        </Badge>
                                    </HStack>

                                    <SimpleGrid columns={[1, 3]} gap={4}>
                                        <Box p={4} bg="#F0FDF4" border="2px solid #0A3D2E" borderRadius="xl" boxShadow="3px 3px 0px #0A3D2E">
                                            <Text fontSize="xs" color="gray.500" fontWeight="bold">TOTAL COLLECTED (TERM 2)</Text>
                                            <Text fontSize="2xl" fontWeight="extrabold" color="brand.800" mt={1}>₦8,450,000</Text>
                                            <Text fontSize="xs" color="emerald.700" fontWeight="semibold" mt={1}>88% of projected target</Text>
                                        </Box>
                                        <Box p={4} bg="#FFFBEB" border="2px solid #0A3D2E" borderRadius="xl" boxShadow="3px 3px 0px #0A3D2E">
                                            <Text fontSize="xs" color="gray.500" fontWeight="bold">PENDING PAYMENTS</Text>
                                            <Text fontSize="2xl" fontWeight="extrabold" color="amber.700" mt={1}>₦1,150,000</Text>
                                            <Text fontSize="xs" color="gray.500" mt={1}>SMS reminders queued</Text>
                                        </Box>
                                        <Box p={4} bg="#EFF6FF" border="2px solid #0A3D2E" borderRadius="xl" boxShadow="3px 3px 0px #0A3D2E">
                                            <Text fontSize="xs" color="gray.500" fontWeight="bold">DEDICATED ACCOUNT</Text>
                                            <Text fontSize="lg" fontWeight="extrabold" color="blue.900" mt={1}>Wema · 9823410928</Text>
                                            <Text fontSize="xs" color="blue.700" mt={1}>Direct payout to school wallet</Text>
                                        </Box>
                                    </SimpleGrid>

                                    {/* Recent payments table */}
                                    <Box border="2px solid #0A3D2E" borderRadius="xl" overflow="hidden">
                                        <Box as="table" w="full" fontSize="xs">
                                            <Box as="thead" bg="#F1EFE9" borderBottom="2px solid #0A3D2E">
                                                <Box as="tr" textAlign="left" fontWeight="bold" color="gray.800">
                                                    <Box as="th" p={2.5}>Student</Box>
                                                    <Box as="th" p={2.5}>Category</Box>
                                                    <Box as="th" p={2.5}>Amount</Box>
                                                    <Box as="th" p={2.5}>Channel</Box>
                                                    <Box as="th" p={2.5}>Status</Box>
                                                </Box>
                                            </Box>
                                            <Box as="tbody">
                                                {[
                                                    { name: "Amara Okoye", cat: "Tuition & Uniform", amt: "₦85,000", chan: "Direct Bank Transfer", status: "Successful" },
                                                    { name: "Tunde Adeyemi", cat: "Tuition (Term 2)", amt: "₦65,000", chan: "Virtual Account", status: "Successful" },
                                                    { name: "Fatima Bello", cat: "Exam & Science Lab", amt: "₦25,000", chan: "USSD Transfer", status: "Successful" },
                                                ].map((pay) => (
                                                    <Box key={pay.name} as="tr" borderBottom="1px solid #E2E8F0" bg="white">
                                                        <Box as="td" p={2.5} fontWeight="bold">{pay.name}</Box>
                                                        <Box as="td" p={2.5} color="gray.600">{pay.cat}</Box>
                                                        <Box as="td" p={2.5} fontWeight="bold" color="brand.800">{pay.amt}</Box>
                                                        <Box as="td" p={2.5} color="gray.500">{pay.chan}</Box>
                                                        <Box as="td" p={2.5}>
                                                            <Badge colorScheme="green" px={2} py={0.5} borderRadius="md">{pay.status}</Badge>
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    </Box>
                                </VStack>
                            )}
                        </Box>

                        {/* Call to action */}
                        <HStack gap={4} justify="center" flexWrap="wrap" pt={4}>
                            <Button
                                size="lg"
                                bg="brand.800"
                                color="white"
                                px={8}
                                fontWeight="bold"
                                borderRadius="xl"
                                border="2px solid #06231A"
                                boxShadow="3px 3px 0px #06231A"
                                transition="all 0.15s ease"
                                _hover={{ bg: "brand.900", transform: "translate(-2px, -2px)", boxShadow: "5px 5px 0px #06231A" }}
                                onClick={() => (window.location.href = APP_REGISTER_URL)}
                            >
                                Get Started Free Today <Icon as={FaArrowRight} ml={2} />
                            </Button>
                            <Button
                                size="lg"
                                bg="white"
                                border="2px solid #0A3D2E"
                                boxShadow="3px 3px 0px #0A3D2E"
                                color="brand.800"
                                px={8}
                                fontWeight="bold"
                                borderRadius="xl"
                                transition="all 0.15s ease"
                                _hover={{ bg: "brand.50", transform: "translate(-2px, -2px)", boxShadow: "5px 5px 0px #0A3D2E" }}
                                onClick={() => navigate("/contact")}
                            >
                                Book a 1-on-1 Guided Demo
                            </Button>
                        </HStack>
                    </VStack>
                </Container>
            </Box>

            {/* Feature Checklist with doodle stickers */}
            <Box py={16} bg="#FAF8F2" borderTop="2px solid #0A3D2E">
                <Container maxW="6xl">
                    <VStack gap={8}>
                        <VStack gap={2} textAlign="center">
                            <Heading as="h2" fontSize={["xl", "2xl"]} fontWeight="extrabold" color="gray.900">
                                Included in every Result subscription
                            </Heading>
                            <Text fontSize="sm" color="gray.600">
                                Everything needed to manage academic records and finance without headache.
                            </Text>
                        </VStack>

                        <SimpleGrid columns={[1, 2, 3]} gap={5} w="full">
                            {[
                                "Continuous Assessment (CA1, CA2, Exam) weights",
                                "Customizable grade scales (A1 to F9 or letter grades)",
                                "Bulk PDF report card generation & download",
                                "Affective & Psychomotor behavioral evaluations",
                                "Dedicated Nigerian bank virtual accounts",
                                "Automatic payment reconciliation & fee tracking",
                                "Parent portal for viewing results & receipts",
                                "Staff role permissions (Viewer, Teacher, Editor, Admin)",
                                "CSV & Excel bulk student data import",
                            ].map((feature) => (
                                <HStack
                                    key={feature}
                                    gap={3}
                                    bg="white"
                                    p={4}
                                    borderRadius="xl"
                                    border="1.5px solid #0A3D2E"
                                    boxShadow="2px 2px 0px #0A3D2E"
                                    transition="all 0.15s ease"
                                    _hover={{ transform: "translate(-1px, -1px)", boxShadow: "3px 3px 0px #0A3D2E" }}
                                >
                                    <Icon as={FaCheckCircle} color="brand.700" fontSize="md" flexShrink={0} />
                                    <Text fontSize="sm" fontWeight="semibold" color="gray.800">{feature}</Text>
                                </HStack>
                            ))}
                        </SimpleGrid>
                    </VStack>
                </Container>
            </Box>

            <PublicFooter />
        </Box>
    );
};

export default DemoPage;
